<template>
  <themed-modal
    name="manage-scenarios"
    title="Manage KSD Scenarios"
    size="lg"
    @closed="handleClosed"
  >
    <div class="manage-scenarios-modal">
      <!-- Scenario Selector -->
      <div class="manage-scenarios-modal__header">
        <div class="manage-scenarios-modal__selector">
          <label class="manage-scenarios-modal__label">Scenarios</label>
          <select 
            v-model="selectedScenario" 
            class="manage-scenarios-modal__select"
            aria-label="Select scenario"
          >
            <option 
              v-for="scenario in allScenarios" 
              :key="scenario.id" 
              :value="scenario.id"
            >
              {{ scenario.name }}
            </option>
          </select>
        </div>
        
        <div class="manage-scenarios-modal__actions">
          <themed-button
            v-if="allScenarios.length > 1"
            @click="confirmDeleteScenario"
            variant="ghost"
            size="icon"
            aria-label="Delete scenario"
          >
            <theme-icon icon="trash-icon" type="icons" classes="w-5 h-5" />
          </themed-button>
          
          <themed-button
            @click="createScenario"
            variant="ghost"
            size="icon"
            aria-label="Add scenario"
          >
            <theme-icon icon="plus-icon" type="icons" classes="w-5 h-5" />
          </themed-button>
        </div>
      </div>

      <!-- Scenario Form -->
      <div class="manage-scenarios-modal__form">
        <div class="manage-scenarios-modal__field">
          <label class="manage-scenarios-modal__field-label">Name</label>
          <themed-input
            v-model="scenarioData.name"
            type="text"
            placeholder="Scenario name"
            class="manage-scenarios-modal__input"
          />
        </div>

        <div class="manage-scenarios-modal__field">
          <div class="manage-scenarios-modal__field-info">
            <label class="manage-scenarios-modal__field-label">Default Scenario</label>
            <p class="manage-scenarios-modal__field-description">
              You can have one default scenario. The default scenario will be used to calculate base scores for your Comps or Tour.
            </p>
          </div>
          <div class="manage-scenarios-modal__toggle">
            <toggle-button 
              v-model="scenarioData.is_default" 
              color="#38C172" 
              sync
            />
          </div>
        </div>

        <div v-if="$gate.isAdmin()" class="manage-scenarios-modal__field">
          <div class="manage-scenarios-modal__field-info">
            <label class="manage-scenarios-modal__field-label">Negotiation Mode</label>
            <p class="manage-scenarios-modal__field-description">
              When negotiation mode is turned on Comp addresses will be hidden from view when viewing your project.
            </p>
          </div>
          <div class="manage-scenarios-modal__toggle">
            <toggle-button 
              v-model="scenarioData.negotiation_mode" 
              color="#38C172" 
              sync
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <themed-button
          variant="secondary"
          @click="hideModal"
        >
          Close
        </themed-button>
        <themed-button
          variant="default"
          @click="save"
        >
          Save
        </themed-button>
      </div>
    </template>
  </themed-modal>
</template>

<script>
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'ManageScenariosModal',

  components: {
    ThemedModal,
    ThemedButton,
    ThemedInput,
    ThemeIcon
  },

  props: {
    ksdableId: {
      type: Number,
      required: true
    },
    ksdableType: {
      type: String,
      required: true
    },
    scenarios: {
      type: Array,
      default: () => []
    },
    scenario: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      allScenarios: [...this.scenarios],
      selectedScenario: this.scenario.id
    };
  },

  computed: {
    scenarioData() {
      if (this.selectedScenario === undefined) {
        return this.allScenarios[0] || {};
      }
      return this.allScenarios.find(s => s.id === this.selectedScenario) || {};
    }
  },

  methods: {
    createScenario() {
      this.allScenarios.push({
        id: 'new',
        name: 'New Scenario',
        ksds: [],
        is_default: 0,
        negotiation_mode: 0,
        ksdable_id: this.ksdableId,
        ksdable_type: this.ksdableType
      });
      this.selectedScenario = 'new';
    },

    save() {
      const url = this.scenarioData.id === 'new' 
        ? '/ksds/scenarios' 
        : `/ksds/scenarios/${this.scenarioData.id}`;
      
      this.scenarioData._method = this.scenarioData.id === 'new' ? 'POST' : 'PATCH';

      axios.post(url, this.scenarioData)
        .then((response) => {
          // Remove the temporary 'new' scenario if it exists
          const newIndex = this.allScenarios.findIndex(s => s.id === 'new');
          if (newIndex !== -1) {
            this.allScenarios.splice(newIndex, 1);
          }
          
          // Add or update the scenario
          const existingIndex = this.allScenarios.findIndex(s => s.id === response.data.scenario.id);
          if (existingIndex !== -1) {
            this.$set(this.allScenarios, existingIndex, response.data.scenario);
          } else {
            this.allScenarios.push(response.data.scenario);
          }

          this.selectedScenario = response.data.scenario.id;

          this.$notify({
            type: 'success',
            title: 'Success',
            text: response.data.message
          });

          // Emit event to parent to update scenarios
          this.$emit('scenarios-updated', this.allScenarios);

          this.hideModal();
        })
        .catch((error) => {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Failed to save scenario'
          });
        });
    },

    removeKSDScenario() {
      this.allScenarios = this.allScenarios.filter(s => s.id !== this.selectedScenario);
      this.selectedScenario = this.allScenarios[0]?.id;
    },

    confirmDeleteScenario() {
      if (this.allScenarios.length === 1) {
        return this.$swal({
          title: 'Cannot Delete Last Scenario',
          text: 'You must have at least one KSD scenario',
          type: 'warning',
          buttonsStyling: false,
          confirmButtonText: 'Ok',
          confirmButtonClass: 'btn btn-green'
        });
      }

      if (this.selectedScenario === 'new') {
        this.removeKSDScenario();
        return;
      }

      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to delete this Scenario?',
        type: 'warning',
        buttonsStyling: false,
        confirmButtonText: 'Yes - Delete it',
        confirmButtonClass: 'btn btn-red',
        showCancelButton: true,
        cancelButtonClass: 'btn btn-secondary mr-4',
        reverseButtons: true
      }).then((result) => {
        if (result.value) {
          this.deleteScenario();
        }
      });
    },

    deleteScenario() {
      axios.delete(`/ksds/scenarios/${this.selectedScenario}`)
        .then((response) => {
          this.$notify({
            type: 'success',
            title: 'Success',
            text: response.data.message
          });

          this.removeKSDScenario();
          
          // Emit event to parent to update scenarios
          this.$emit('scenarios-updated', this.allScenarios);
        })
        .catch((error) => {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Failed to delete scenario'
          });
        });
    },

    hideModal() {
      this.$modal.hide('manage-scenarios');
    },

    handleClosed() {
      // Reset to original scenarios on close without save
      this.allScenarios = [...this.scenarios];
      this.selectedScenario = this.scenario.id;
    }
  }
};
</script>
